import React, { useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ConstructionIcon from '@mui/icons-material/Construction';
import useSWR from 'swr';
import Api from '../../api';
import {
  ServiceRequestStatus,
  type ServiceRequestDTO
} from '../../api/serviceRequest';
import {
  mainColors,
  secondaryColors,
  alertColors,
  successColors,
  accentColors
} from '../../theme/themeConfig';

const STATUS_COLORS: Record<string, string> = {
  authorized: mainColors.MAIN_6,
  cancelled: secondaryColors.SECONDARY,
  completed: successColors.SUCCESS,
  dispatched: mainColors.MAIN,
  in_progress: accentColors.ACCENT,
  invoiced: successColors.SUCCESS_5,
  pending_authorization: '#8B5CF6',
  pending_review: '#F59E0B',
  rejected: alertColors.ALERT
};

const PRIORITY_COLORS: Record<string, string> = {
  high: '#F59E0B',
  low: secondaryColors.SECONDARY,
  medium: mainColors.MAIN,
  urgent: alertColors.ALERT
};

const Card = styled(Box)(() => ({
  background: '#fff',
  border: `1px solid ${secondaryColors.SECONDARY_6}`,
  borderRadius: '10px',
  marginBottom: '10px',
  padding: '14px 16px'
}));

function formatDate(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}

function formatDateTime(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    month: 'short'
  });
}

function getClientName(sr: ServiceRequestDTO): string {
  if (sr.client?.businessName) return sr.client.businessName;
  const parts = [sr.client?.firstName, sr.client?.lastName].filter(Boolean);
  return parts.join(' ') || sr.client?.email || '—';
}

const MyServiceRequestsWidget: React.FC<{ onSelect?: (id: string) => void }> =
  function MyServiceRequestsWidget({ onSelect }) {
    const { data: srList = [], error } = useSWR(
      'my-service-request-assignments',
      () => Api.ServiceRequestApi.getMyAssignments()
    );

    const isLoading = srList.length === 0 && !error;

    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: '24px' }}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (srList.length === 0) {
      return (
        <Box
          sx={{
            border: `1px dashed ${secondaryColors.SECONDARY_6}`,
            borderRadius: '10px',
            color: '#CBD5E1',
            fontSize: '13px',
            py: '24px',
            textAlign: 'center'
          }}
        >
          No service requests assigned
        </Box>
      );
    }

    return (
      <Box>
        {(srList as ServiceRequestDTO[]).map((sr) => {
          const statusColor =
            STATUS_COLORS[sr.status] ?? secondaryColors.SECONDARY;
          const priorityColor = PRIORITY_COLORS[sr.priority] ?? mainColors.MAIN;
          const myAssignment = (sr.assignments ?? []).find(
            (a) => a.scheduledStart
          );

          return (
            <Card
              key={sr.id}
              onClick={() => onSelect?.(sr.id)}
              sx={{ cursor: onSelect ? 'pointer' : 'default' }}
            >
              {/* Priority bar */}
              <Box
                sx={{
                  background: priorityColor,
                  borderRadius: '3px',
                  height: '3px',
                  mb: '10px',
                  width: '100%'
                }}
              />

              <Box
                sx={{
                  alignItems: 'flex-start',
                  display: 'flex',
                  justifyContent: 'space-between'
                }}
              >
                <Box sx={{ flex: 1, pr: '12px' }}>
                  <Typography
                    sx={{ fontSize: '14px', fontWeight: 700, lineHeight: 1.4 }}
                  >
                    {sr.title}
                  </Typography>
                  <Typography
                    sx={{
                      color: secondaryColors.SECONDARY,
                      fontSize: '12px',
                      mt: '2px'
                    }}
                  >
                    {getClientName(sr)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    alignItems: 'flex-end',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px'
                  }}
                >
                  <Chip
                    label={sr.status.replace(/_/g, ' ')}
                    size="small"
                    sx={{
                      background: `${statusColor}18`,
                      color: statusColor,
                      fontSize: '10px',
                      fontWeight: 700,
                      height: '20px',
                      textTransform: 'capitalize'
                    }}
                  />
                  <Chip
                    label={sr.priority}
                    size="small"
                    sx={{
                      background: `${priorityColor}18`,
                      color: priorityColor,
                      fontSize: '10px',
                      fontWeight: 600,
                      height: '20px',
                      textTransform: 'capitalize'
                    }}
                  />
                </Box>
              </Box>

              {sr.description && (
                <Typography
                  sx={{
                    color: '#64748B',
                    fontSize: '12px',
                    mt: '6px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {sr.description}
                </Typography>
              )}

              <Divider sx={{ my: '10px' }} />

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                {sr.requestedDate && (
                  <Box>
                    <Typography
                      sx={{
                        color: secondaryColors.SECONDARY,
                        fontSize: '10px',
                        fontWeight: 600,
                        textTransform: 'uppercase'
                      }}
                    >
                      Requested
                    </Typography>
                    <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>
                      {formatDate(sr.requestedDate)}
                    </Typography>
                  </Box>
                )}

                {myAssignment?.scheduledStart && (
                  <Box>
                    <Typography
                      sx={{
                        color: secondaryColors.SECONDARY,
                        fontSize: '10px',
                        fontWeight: 600,
                        textTransform: 'uppercase'
                      }}
                    >
                      Scheduled
                    </Typography>
                    <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>
                      {formatDateTime(myAssignment.scheduledStart)}
                    </Typography>
                  </Box>
                )}

                {sr.estimatedDurationMinutes && (
                  <Box>
                    <Typography
                      sx={{
                        color: secondaryColors.SECONDARY,
                        fontSize: '10px',
                        fontWeight: 600,
                        textTransform: 'uppercase'
                      }}
                    >
                      Duration
                    </Typography>
                    <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>
                      {sr.estimatedDurationMinutes} min
                    </Typography>
                  </Box>
                )}

                {Number(sr.totalAmount) > 0 && (
                  <Box>
                    <Typography
                      sx={{
                        color: secondaryColors.SECONDARY,
                        fontSize: '10px',
                        fontWeight: 600,
                        textTransform: 'uppercase'
                      }}
                    >
                      Value
                    </Typography>
                    <Typography
                      sx={{
                        color: mainColors.MAIN,
                        fontSize: '12px',
                        fontWeight: 700
                      }}
                    >
                      ${Number(sr.totalAmount).toFixed(2)}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Line items summary */}
              {(sr.lineItems ?? []).length > 0 && (
                <Box
                  sx={{
                    background: '#F8FAFC',
                    borderRadius: '6px',
                    mt: '10px',
                    p: '8px 12px'
                  }}
                >
                  {(sr.lineItems ?? []).map((item) => (
                    <Box
                      key={item.id}
                      sx={{
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: '2px'
                      }}
                    >
                      <Typography sx={{ color: '#374151', fontSize: '11px' }}>
                        {item.description}
                        {item.unit && (
                          <span style={{ color: '#94A3B8' }}>
                            {' '}
                            / {item.unit}
                          </span>
                        )}
                      </Typography>
                      <Typography
                        sx={{
                          color: '#374151',
                          fontSize: '11px',
                          fontWeight: 600
                        }}
                      >
                        {item.quantity} × ${Number(item.unitPrice).toFixed(2)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Card>
          );
        })}
      </Box>
    );
  };

MyServiceRequestsWidget.defaultProps = { onSelect: undefined };
export default MyServiceRequestsWidget;
